import Analytics from "../Analytics";
import Session from "session";
import get from "lodash/get";
import map from "lodash/map";
import isEmpty from "lodash/isEmpty";
import isPlainObject from "lodash/isPlainObject";
import isFunction from "lodash/isFunction";
import moment from "moment";
import {
  getURLParams,
  isMobile,
  compactJoin,
  lower,
  flattenObject,
  removeSensitiveData,
  getPromocodeBenefits
} from "utils/helpers";
import {
  ANALYTICS_EVENT_TYPES,
  ANALYTICS_TYPES,
  CLEVERTAP_EVENTS,
  CLEVERTAP_ALLOWED_EVENTS
} from "constants/AnalyticsTypeConstants";
import {
  PRODUCT_TYPE,
  CONTRACT_TYPES,
  CONTRACT_ITEM_TYPES,
  SIM_TYPES,
  paths,
  ROOT_CHANNEL,
  WEB_CHANNEL,
  PLAN_TYPE,
  PAYMENT_STATUSES_CXOS,
  MANAGE_ORDER_URL
} from "utils/constants";
import { getOrderRef, getItem, setItem } from "utils/localStorage";
import { getStateData } from "analytics/index";

const CLEVERTAP_TIMEOUT = 60000;
const CLEVERTAP_INTERVAL = 1000;

class CleverTapAnalytics extends Analytics {
  logStart(event, info, data) {
    super.logEventStart(ANALYTICS_TYPES.cleverTap, event, info, data);
  }

  logFinish(event, info, data) {
    super.logEventFinish(ANALYTICS_TYPES.cleverTap, event, info, data);
  }

  trackPage() {
    super.trackPage(ANALYTICS_TYPES.cleverTap);
  }

  pushEvent(event, data, message, reduxStore) {
    this.trackPage();
    this.onClevertapDefined(() => {
      this.logStart(ANALYTICS_EVENT_TYPES.PUSH_EVENT, event);
      const eventData = this.constructEventData(event, data, reduxStore);
      /* Stopped pushing the all events to clevertap*/

      // if (!isEmpty(eventData)) {
      //   window.clevertap.event.push(event, eventData);
      //   console.log(`Pushed CleverTap Analytics Event: ${event}`, eventData);
      //   this.logFinish(ANALYTICS_EVENT_TYPES.PUSH_EVENT, event, eventData);
      // }

      if (eventData.customEvent) {
        this.logStart(
          ANALYTICS_EVENT_TYPES.PUSH_EVENT,
          eventData.customEvent,
          eventData
        );
        window.clevertap.event.push(eventData.customEvent, eventData);
        this.logFinish(
          ANALYTICS_EVENT_TYPES.PUSH_EVENT,
          eventData.customEvent,
          eventData
        );
      }
      if (eventData.charged) {
        this.pushChargedEvent(data);
      }
    });
  }

  pushChargedEvent(data) {
    this.logStart(ANALYTICS_EVENT_TYPES.PUSH_EVENT, CLEVERTAP_EVENTS.CHARGED);
    const chargedData = this.constructChargedData(data);
    if (!isEmpty(chargedData)) {
      window.clevertap.event.push(CLEVERTAP_EVENTS.CHARGED, chargedData);
      console.log("Pushed CleverTap Charged Event ", chargedData);
      if (
        lower(get(chargedData, "Payment Status")) === "success" ||
        get(data, "status") === "so_generated"
      ) {
        const profileData = { Site: { isProspect: false } };
        this.logStart(
          ANALYTICS_EVENT_TYPES.PUSH_PROFILE,
          CLEVERTAP_EVENTS.PROFILE
        );
        window.clevertap.profile.push(profileData);
        console.log("Pushed CleverTap Profile ", profileData);
        this.logFinish(
          ANALYTICS_EVENT_TYPES.PUSH_PROFILE,
          CLEVERTAP_EVENTS.PROFILE,
          profileData
        );
      }
    }
    this.logFinish(
      ANALYTICS_EVENT_TYPES.PUSH_EVENT,
      CLEVERTAP_EVENTS.CHARGED,
      chargedData
    );
  }

  pushProfile(_, data, message, defaultAnalytics) {
    this.onClevertapDefined(() => {
      this.logStart(
        ANALYTICS_EVENT_TYPES.PUSH_PROFILE,
        CLEVERTAP_EVENTS.PROFILE
      );
      const {
        forms,
        field,
        telco_info: telcoInfo = {},
        referral_code_type,
        external_id
      } = data;
      const formValues = get(forms, "UserLoginForm.values", {});
      const onUserLoginData = !isEmpty(telcoInfo)
        ? this.constructProfileDataFromUserServiceTelcoInfo({
            ...telcoInfo,
            referral_code_type
          })
        : this.constructProfileData(formValues, defaultAnalytics, external_id);
      if (!isEmpty(onUserLoginData)) {
        console.log("UserLoginData", onUserLoginData);
        window.clevertap.onUserLogin.push(onUserLoginData);
        console.log("Pushed User Profile: ", onUserLoginData);
        this.logFinish(
          ANALYTICS_EVENT_TYPES.PUSH_PROFILE,
          CLEVERTAP_EVENTS.PROFILE,
          onUserLoginData
        );
      }

      field &&
        setTimeout(() => {
          const fieldEvent = `ecomm_${field}_entered`;
          this.logStart(ANALYTICS_EVENT_TYPES.PUSH_EVENT, fieldEvent);
          console.log("CleverTap event:", fieldEvent);
          window.clevertap.event.push(fieldEvent);
          this.logFinish(ANALYTICS_EVENT_TYPES.PUSH_EVENT, fieldEvent);
        }, 2000);
    });
  }

  constructProfileData = (
    {
      loggedInUserEmail = "",
      email = "",
      country_code = "",
      contact_number = "",
      dob = "",
      full_name = "",
      first_name = "",
      middle_name = "",
      last_name = "",
      sim_count = "",
      referral_code_type: CouponCode_Type = "",
      referral_code: CouponCode = ""
    } = {},
    defaultAnalytics,
    externalId
  ) => {
    const userEmail = email || loggedInUserEmail;
    if (!userEmail) return {}; // email is mandatory for profile data
    const fullName = compactJoin([first_name, middle_name, last_name], " ");
    const name = full_name || fullName;
    const phone = `${country_code}${contact_number}`;
    const DOB = dob && new Date(moment(dob, "DD-MM-YYYY").toISOString());

    return {
      Site: {
        Name: name,
        Phone: phone,
        DOB,
        Identity: externalId,
        Email: userEmail,
        email_address: userEmail,
        "MSG-email": get(
          defaultAnalytics,
          "notificationDefaultPref.email",
          true
        ),
        "MSG-push": get(defaultAnalytics, "notificationDefaultPref.push", true),
        "MSG-sms": get(defaultAnalytics, "notificationDefaultPref.sms", true),
        phone_number: phone,
        profilesource: "ecomm",
        Lifecycle_Stage: "lead_ecomm",
        isProspect: !getOrderRef(),
        orderRef: getOrderRef(),
        CouponCode,
        CouponCode_Type,
        ...(sim_count && { sim_count })
      }
    };
  };

  constructProfileDataFromUserServiceTelcoInfo = ({
    isd_code,
    phone_no,
    circles_info: circlesInfo = {},
    service_instance_no: sin = "",
    service_account_email: email = "",
    referral_code_type: CouponCode_Type = ""
  } = {}) => {
    const {
      order_reference_number: orderRef = "",
      referral_code: CouponCode = "",
      service_instance_base_plan_name: planName = "",
      user_name: userName = {}
    } = circlesInfo;
    const { name: { first = "", last = "", middle = "" } = {} } = userName;

    if (!email) return {}; // email is mandatory for profile data
    const name = compactJoin([first, middle, last], " ");
    const phone = `${isd_code}${phone_no}`;

    return {
      Site: {
        Name: name,
        Phone: phone,
        Identity: sin || email,
        Email: email,
        profilesource: "ecomm",
        Lifecycle_Stage: sin ? "customer" : "lead_ecomm",
        isProspect: !orderRef,
        orderRef,
        CouponCode,
        CouponCode_Type,
        planName
      }
    };
  };

  constructChargedData = data => {
    const { totalOrderValue, purchaseID, customerID, paymentStatus, discount } =
      get(data, "order_product_info") || {};
    const amount =
      totalOrderValue ||
      get(data, "orderParams.order_summary.onetime_total_fee");

    const orderItems = get(
      data,
      "orderParams.order_items",
      get(data, "orderParams.order_items_attributes", [])
    );
    const { order_ref, referral_code, self_activation } = data;

    const chargedID = purchaseID || get(data, "order.order_ref") || order_ref;
    const {
      promoCode,
      promoCodeDiscountAmt,
      promoCodeCategory,
      promoCodeDescription
    } = discount || {};

    let Items = map(
      get(data, "order_product_info.item"),
      this.constructChargedItem
    );

    if (!isEmpty(orderItems)) {
      Items = Items.concat(
        map(orderItems, ({ title, price, sku }) => {
          return {
            Name: title,
            Category: PRODUCT_TYPE.addon,
            Type: PRODUCT_TYPE.addon,
            price,
            sku: sku
          };
        })
      );
    }

    const portInDetails = get(
      data,
      "orderParams.portin_detail",
      get(data, "orderParams.port_in_details", {})
    );
    const deliveryDetails = get(data, "orderParams.delivery_slot", {});
    const multiSimDetails = get(data, "orderParams.multi_sim_attributes", {});
    const selected_number =
      get(data, "orderParams.selected_number") ||
      get(data, "orderParams.selected_number_uniq_index");
    const id_upload_pending = get(data, "orderParams.id_upload_pending");

    const { sim_count: familySimCount } = get(data, "orderParams", {});
    const {
      phone_details,
      device_installment_month_fee,
      device_installment_months,
      phone_payment_option,
      onetime_device_fee,
      device_installment_total_fee
    } = get(data, "orderParams.order_summary", {});

    const { delivery_method, delivery_slot_full_data } = deliveryDetails;
    const { multi_sim_order, multi_sim_quantity } = multiSimDetails;
    const {
      port_donor,
      port_number,
      port_auth_pending,
      port_id_number
    } = portInDetails;

    if (!chargedID) return {};
    let chargedObj = {
      "Payment mode": "Credit Card",
      "Charged ID": chargedID,
      "Customer ID": customerID || Session.getDeviceId(),
      "Payment Status": paymentStatus || get(data, "payment_status"),
      "Promo Code": promoCode || get(data, "referral_code") || referral_code,
      "Promo Code Discount Amount":
        promoCodeDiscountAmt ||
        get(
          data,
          "orderParams.order_summary.onetime_referral_code_discount_fee"
        ),
      "Promo Code Category": promoCodeCategory || "",
      "Promo Code Description": promoCodeDescription || "",
      Channel: get(data, "channel") || get(data, "orderParams.channel_name"),
      PlanName: get(data, "plan_name"),
      Amount: amount,
      ...(phone_details && {
        phone_purchased: phone_details,
        ...(phone_payment_option === "full" && {
          phones_payment_upfront: onetime_device_fee
        }),
        ...(phone_payment_option === "split" && {
          phones_payment_term: device_installment_months,
          phones_payment_split: device_installment_month_fee,
          phone_payment_split_upfront: onetime_device_fee,
          total_phone_installment_cost: device_installment_total_fee
        }),
        ...(phone_payment_option === "installment" && {
          phones_payment_term: device_installment_months,
          phones_payment_ipp: device_installment_month_fee,
          total_phone_installment_cost: device_installment_total_fee
        })
      }),
      ...(id_upload_pending && { id_upload_pending }),
      ...(!isEmpty(portInDetails) && {
        port_in: true,
        port_in_number: port_number || port_id_number,
        port_donor,
        port_in_under_nric: port_auth_pending === "false"
      }),
      ...(!isEmpty(deliveryDetails) &&
        !id_upload_pending && {
          delivery_method: delivery_method,
          delivery_time: delivery_slot_full_data
        }),
      ...(isEmpty(portInDetails) && selected_number && { selected_number }),
      ...(familySimCount && { family_sim_count: familySimCount }),
      ...(multi_sim_order && { referral_sim_count: multi_sim_quantity }),
      ...(self_activation && { self_activation })
    };

    for (let key of Items) {
      if (
        key.Type === PRODUCT_TYPE.addon &&
        key.name !== PRODUCT_TYPE.nilclass
      ) {
        chargedObj[key.Name] = 1;
      }
    }

    return chargedObj;
  };

  constructChargedItem = (item, planName) => {
    const {
      quantity: Quantity = 1,
      price,
      productInfo,
      sku,
      name,
      title,
      item_type
    } = item;
    const Price = get(price, "basePrice", 0);
    const {
      productName: Name = name || title,
      productCategory: Category = item_type || "ADDON",
      productType: Type = item_type || "ADDON",
      planName: Plan = planName,
      productDiscount: Discount = 0,
      productID: ID = sku,
      productBrand: Brand = "LibertyWireless",
      productSize: Size = "NA",
      productColour: Colour = "NA",
      productDescription: Description = "NA"
    } = productInfo || {};

    return {
      Name,
      Category,
      Type,
      Plan,
      Discount,
      Quantity,
      Price,
      ID,
      Brand,
      Size,
      Colour,
      Description
    };
  };

  onClevertapDefined = pushDataToClevertap => {
    if (!window.clevertapDisabled) {
      const intervalID = setInterval(() => {
        // check if clevertapDisabled is set in between and stop polling
        if (window.clevertapDisabled) {
          console.log("clevertapDisabled, stopped polling...");
          window.clearInterval(intervalID);
          return;
        }
        const cleverTapDefined = window.clearOldCleverTapIDStarted
          ? window.clearOldCleverTapIDDone
          : window.clevertap;
        if (cleverTapDefined) {
          pushDataToClevertap();
          window.clearInterval(intervalID);
        } else {
          console.log("cleverTapDefined is false, delaying...");
        }
      }, CLEVERTAP_INTERVAL);
      setTimeout(() => clearInterval(intervalID), CLEVERTAP_TIMEOUT);
    }
  };

  clearOldCleverTapID = () => {
    if (!window.clevertapDisabled) {
      console.log("clearOldCleverTapIDStarted");
      window.clearOldCleverTapIDStarted = true;
      const intervalID = setInterval(() => {
        // check if clevertapDisabled is set in between and stop polling
        if (window.clevertapDisabled) {
          console.log("clevertapDisabled, stopped polling...");
          window.clearInterval(intervalID);
          return;
        }
        if (window.clevertap?.getCleverTapID) {
          const cleverTapID = window.clevertap.getCleverTapID();
          console.log("OldCleverTapID: ", cleverTapID);
          if (cleverTapID) {
            console.log(
              "clearOldCleverTapID: start ",
              window.clevertap.getCleverTapID()
            );
            isFunction(window.clevertap.clear) && window.clevertap.clear();
            console.log(
              "clearOldCleverTapID: end ",
              window.clevertap.getCleverTapID()
            );
          }
          window.clearOldCleverTapIDDone = true;
          window.clearInterval(intervalID);
        } else {
          console.log("getCleverTapID undefined, delaying...");
        }
      }, CLEVERTAP_INTERVAL);
      setTimeout(() => clearInterval(intervalID), CLEVERTAP_TIMEOUT);
    }
  };

  constructEventData = (event, data = {}, reduxStore) => {
    let eventData = this.getEventData(event, data, reduxStore);
    if (!eventData) return {};
    eventData = isPlainObject(eventData) ? eventData : { eventData };
    return {
      ...eventData,
      ...getURLParams(),
      env: isMobile() ? "mobile" : "desktop",
      host: window.location.host,
      path: window.location.pathname,
      sessionId: get(eventData, "customer_id") || Session.getDeviceId()
      // ipAddress: Session.getIpAddress()
    };
  };

  getEventData = (event, data, reduxStore) => {
    if (!CLEVERTAP_ALLOWED_EVENTS.includes(event)) return null;
    let eventData = {};
    switch (event) {
      case "page_visit":
        {
          let cleverTapEventName = "";
          let cleverTapProperties = {};

          const currentPath = window.location.pathname;
          const currentUrl = window.location.origin + currentPath;
          if (
            currentPath ===
            get(paths, "PRE_CHECKOUT").replace(ROOT_CHANNEL, WEB_CHANNEL)
          ) {
            cleverTapEventName = CLEVERTAP_EVENTS.PURCHASE_FLOW_STARTED;
          }

          if (
            currentPath ===
            get(paths, "PAYMENT_SUCCESS", "").replace(ROOT_CHANNEL, WEB_CHANNEL)
          ) {
            const customer_id = getStateData(
              reduxStore,
              "api.loggedInUserDetails.data.external_id",
              ""
            );
            const order_id = getStateData(
              reduxStore,
              "api.order.data.orderReference",
              ""
            );
            cleverTapEventName = CLEVERTAP_EVENTS.ORDER_CONFIRMATION;
            cleverTapProperties = {
              customer_id,
              order_id
            };
          }

          if (currentPath === get(paths, "MANAGE_MANUAL_EKYC", "")) {
            cleverTapEventName = CLEVERTAP_EVENTS.KYC_STARTED;
          }

          if (currentPath === get(paths, "MANAGE_SIM_ACTIVATION")) {
            let numberType = lower(
              getStateData(reduxStore, "api.guestOrder.data.contract_type")
            );

            if (numberType === lower(get(CONTRACT_TYPES, "NEW")))
              numberType = lower(get(CONTRACT_ITEM_TYPES, "NEW_NUMBER"));

            cleverTapEventName = CLEVERTAP_EVENTS.ACTIVATION_INITIATION;
            cleverTapProperties = {
              number_type: numberType
            };
          }

          if (
            currentPath.includes(
              get(paths, "RESCHEDULE_DELIVERY", "").replace(":order_type?", "")
            )
          ) {
            cleverTapEventName = CLEVERTAP_EVENTS.TAP;
            cleverTapProperties = {
              item_name: "Reschedule Delivery",
              item_location: currentUrl
            };
          }

          eventData = {
            customEvent: cleverTapEventName,
            ...cleverTapProperties
          };
        }
        break;
      case "postpaid_planService_requested":
        eventData = {
          customEvent: CLEVERTAP_EVENTS.PLAN_SELECTED,
          plan_type: get(data, "plan_name")
        };
        break;
      case "eligiblePlans_api_call_requested":
        eventData = {
          customEvent: CLEVERTAP_EVENTS.CATEGORY_SELECTED,
          category_type: get(data, "productCategory")
        };
        break;
      case "addon_selected":
        eventData = {
          customEvent:
            get(data, "productCategory", "") +
            "_" +
            CLEVERTAP_EVENTS.ADDON_SELECTED,
          addon_name: get(data, "addon.title", "")
        };
        break;
      case "addon_deselected":
        {
          // Caters for prepaid/postpaid addon deselection and deletion
          const currentUrl = window.location.pathname;
          let cleverTapEventName =
            get(data, "productCategory", "") +
            "_" +
            CLEVERTAP_EVENTS.ADDON_DESELECTED;

          if (
            currentUrl ===
            get(paths, "SELECTED_PACKAGES", "").replace(
              ROOT_CHANNEL,
              WEB_CHANNEL
            )
          )
            cleverTapEventName = CLEVERTAP_EVENTS.PREPAID_ADDON_DELETED;
          eventData = {
            customEvent: cleverTapEventName,
            addon_name: get(data, "addon.title", "")
          };
        }
        break;
      case "PreCheckoutForm_submitted":
        {
          const previousUrl = window.location.pathTracking.previous;

          const { plan_name, userSelectedAddons, productCategory } = data;
          let cleverTapEventName = "";
          let cleverTapEventProperties = {};
          const selectedPackagesPage = get(
            paths,
            "SELECTED_PACKAGES",
            ""
          ).replace(ROOT_CHANNEL, WEB_CHANNEL);
          const addonSelectionPage = get(paths, "ADDON_SELECTION", "").replace(
            ROOT_CHANNEL,
            WEB_CHANNEL
          );

          if (
            previousUrl === selectedPackagesPage ||
            previousUrl === addonSelectionPage
          ) {
            cleverTapEventName =
              productCategory === get(PLAN_TYPE, "PREPAID")
                ? CLEVERTAP_EVENTS.PREPAID_PACKAGE_SELECTED
                : CLEVERTAP_EVENTS.PLAN_SELECTED_2;
            cleverTapEventProperties = {
              ...(productCategory === get(PLAN_TYPE, "PREPAID") && {
                prepaid_addon_selected: map(
                  userSelectedAddons,
                  userSelectedAddon => get(userSelectedAddon, "title"),
                  []
                ).join(", ")
              }),
              ...(productCategory === get(PLAN_TYPE, "POSTPAID") && {
                plan_type: plan_name,
                addon_selected: map(
                  userSelectedAddons,
                  userSelectedAddon => get(userSelectedAddon, "title"),
                  []
                ).join(", ")
              })
            };
          }

          eventData = {
            customEvent: cleverTapEventName,
            ...cleverTapEventProperties
          };
        }
        break;
      case "payment_method_selected":
        eventData = {
          customEvent: CLEVERTAP_EVENTS.FIELD_TYPE_SELECTED.replace(
            "{field}",
            get(data, "field")
          ).replace("_method", ""),
          payment_type: get(data, "formValues.payment_method.code")
        };
        break;
      case `${get(data, "field")}_selected`:
        {
          const { formValues = {}, field } = data;
          eventData = {
            customEvent: CLEVERTAP_EVENTS.FIELD_TYPE_SELECTED.replace(
              "{field}",
              field
            ),
            [get(data, "field")]:
              get(formValues, `${field}.label`, "") || get(formValues, field)
          };
        }
        break;
      case "userLoginOTP_api_call_succeeded":
        {
          const email = get(data, "forms.UserLoginForm.values.email", "");
          eventData = {
            customEvent: CLEVERTAP_EVENTS.EMAIL_SIGNUP_SUBMIT,
            email
          };
        }
        break;
      case "userAuthenticate_api_call_succeeded":
        {
          const email = get(data, "forms.UserLoginForm.values.email", "");
          eventData = {
            customEvent: CLEVERTAP_EVENTS.VERIFICATION_OTP_SUBMITTED,
            email,
            status: "success"
          };
        }
        break;
      case "userAuthenticate_api_call_failed":
        eventData = {
          customEvent: CLEVERTAP_EVENTS.VERIFICATION_OTP_SUBMITTED,
          status: "failed"
        };
        break;
      case "kyc_api_call_succeeded":
        {
          const currentUrl = window.location.origin + window.location.pathname;
          const reconfirmIdentityNeeded = get(
            data,
            "userOrder.next_step.action",
            []
          ).includes("reconfirm_identity");

          eventData = {
            customEvent: CLEVERTAP_EVENTS.TAP,
            item_name: reconfirmIdentityNeeded
              ? "Reconfirm your Identity"
              : "Confirm your identity",
            item_location: currentUrl
          };
        }
        break;
      case "selectedNumberType_requested":
        {
          let simType = lower(get(data, "sim_type", ""));
          let numberType = lower(get(data, "number_type", ""));
          if (simType === lower(get(SIM_TYPES, "E_SIM")))
            simType = lower(get(SIM_TYPES, "ESIM"));

          if (numberType === lower(get(CONTRACT_TYPES, "NEW")))
            numberType = lower(get(CONTRACT_ITEM_TYPES, "NEW_NUMBER"));

          eventData = {
            customEvent: CLEVERTAP_EVENTS.SIM_NUMBER_TYPE_SELECTED,
            sim_type: simType,
            ...(numberType === lower(get(CONTRACT_ITEM_TYPES, "NEW_NUMBER"))
              ? { number_type: numberType }
              : { telco: get(data, "telco.label", "") })
          };
        }
        break;
      case "numberAndDeliverySlotsAvailability_api_call_succeeded":
        {
          const { plan_name, userSelectedAddons, referral_code } = data;
          eventData = {
            customEvent: CLEVERTAP_EVENTS.ORDER_SUMMARY_COMPLETED,
            plan_type: get(data, "planService.productCategory"),
            plan_name,
            addon_selected: map(
              userSelectedAddons,
              userSelectedAddon => get(userSelectedAddon, "title"),
              []
            ).join(", "),
            promocode_used: referral_code
          };
        }
        break;
      case "paymentStatus_api_call_succeeded":
        {
          let { payment_status, message, payment_options } = data;
          payment_status = lower(payment_status);
          const psp_reference = get(payment_options, "psp_reference", "");
          const paymentMade = getItem("psp_reference");
          if (psp_reference) setItem("psp_reference", psp_reference);

          if (
            (payment_status === lower(get(PAYMENT_STATUSES_CXOS, "SUCCESS")) ||
              payment_status ===
                lower(get(PAYMENT_STATUSES_CXOS, "FAILURE"))) &&
            !paymentMade
          ) {
            eventData = {
              customEvent: CLEVERTAP_EVENTS.PAYMENT_DETAILS_SUBMITTED,
              status: payment_status,
              ...(message &&
                payment_status ===
                  lower(get(PAYMENT_STATUSES_CXOS, "FAILURE")) && {
                  error_reason: message
                })
            };
          }
        }
        break;
      case "paymentReturn_api_call_succeeded":
        {
          const order_ref = data?.order?.order_ref || data?.order_ref;
          const payment_status = data?.payment_status;
          const customer_id = data?.customer_id || Session.getDeviceId();
          eventData = { order_ref, payment_status, customer_id, charged: true };
        }
        break;
      case "simActivated_requested":
        {
          const order_ref = data?.order?.order_ref || data?.order_ref;
          const payment_status = data?.payment_status;
          const customer_id = data?.customer_id || Session.getDeviceId();
          eventData = { order_ref, payment_status, customer_id, charged: true };
        }
        break;
      case "selfActivationDeliveryOrder_api_call_succeeded":
        {
          const {
            order_ref,
            payment_status,
            id: customer_id,
            family_plan_sim_count
          } = data || {};
          eventData = {
            order_ref,
            payment_status,
            customer_id,
            charged: true,
            ...(family_plan_sim_count && { sim_count: family_plan_sim_count })
          };
        }
        break;
      case "numbers_api_call_succeeded":
        eventData = {
          customEvent: CLEVERTAP_EVENTS.SELECTED_NUMBER,
          ...get(data, "selected_number", {})
        };
        break;
      case "referralCode_api_call_succeeded":
        eventData = {
          customEvent: CLEVERTAP_EVENTS.PROMOTION_STATUS,
          ...getPromocodeBenefits(data)
        };
        break;
      case "verifyIdentity_api_call_succeeded":
        eventData = { success: get(data, "success") };
        break;
      case "paymentRequest_api_call_succeeded":
        eventData = { order_ref: get(data, "order_ref") };
        break;
      case "field_blurred":
        {
          const { form, field, payload } = data || {};
          eventData = { form, field, payload };
        }
        break;
      case "PortInForm_submitted":
        eventData = {
          customEvent: CLEVERTAP_EVENTS.SELECTED_NUMBER_PORT_IN,
          ...this.flattenData(get(data, "formValues", {}))
        };
        break;
      case "guestOrder_requested":
        eventData = {
          customEvent: CLEVERTAP_EVENTS.GUEST_ORDER_CLICK,
          button_name: get(data, "button_name", ""),
          action_name: get(data, "action_name", "")
        };
        break;
      case "PersonalDetailsForm_submitted":
        eventData = {
          customEvent: CLEVERTAP_EVENTS.USER_DETAILS_SUBMITTED,
          ...removeSensitiveData(this.flattenData(get(data, "formValues", {})))
        };
        break;
      case "order_api_call_requested":
        eventData = removeSensitiveData(this.flattenData(data));
        break;
      case "appSettings_api_call_requested":
      case "appSettings_api_call_succeeded":
      case "locale_api_call_succeeded":
      case "verifyIdentity_api_call_requested":
      case "screenshot_api_call_requested":
      case "paymentRequest_api_call_requested":
      case "liquidOCRRegister_api_call_requested":
      case "userKycDetails_api_call_requested":
        eventData = {};
        break;
      case "manualEkyc_api_call_succeeded":
        eventData = {
          customEvent: CLEVERTAP_EVENTS.KYC_SUBMITTED
        };
        break;
      case "faq_item_clicked":
        {
          const currentPath = window.location.pathname;
          const currentUrl = window.location.origin + currentPath;

          if (
            currentPath ===
              get(paths, "PAYMENT_SUCCESS", "").replace(
                ROOT_CHANNEL,
                WEB_CHANNEL
              ) ||
            currentPath === MANAGE_ORDER_URL
          ) {
            eventData = {
              customEvent: CLEVERTAP_EVENTS.TAP,
              item_name: "FAQ",
              item_location: currentUrl
            };
          }
        }
        break;
      default:
        eventData = removeSensitiveData(this.flattenData(data));
        break;
    }
    return eventData;
  };

  flattenData = data => flattenObject(data, "", "->");
}

export default new CleverTapAnalytics();
